import type {
  ConnectionCreateIssue,
  ConnectionInventory,
  ConnectionPairingIssue,
} from "@nlkb/api-contract/connections";

import {
  productionApiOrigin,
  productionWebOrigin,
  stagingApiOrigin,
  stagingWebOrigin,
} from "./share-links";

export const localAgentApiOrigin = "http://127.0.0.1:8787";
export const agentOnboardingSkillPath = "/agent-onboarding/SKILL.md";

export type PairingMode = "agent" | "mcp";
export type ConnectionEndpointType = "desktop" | "local" | "service" | "mcp";

export interface ConnectionOwnerGroup {
  readonly owner_principal_id: string;
  readonly owner_display_name: string;
  readonly owner_principal_type: "human" | "service";
  readonly connections: readonly ConnectionInventory[];
}

export function pairingApiOrigin(input: {
  readonly configuredBaseUrl: string;
  readonly pageOrigin: string;
  readonly development: boolean;
}): string {
  if (input.configuredBaseUrl.length > 0) return input.configuredBaseUrl;
  if (input.development) return localAgentApiOrigin;
  return publicApiOriginForPage(input.pageOrigin);
}

function publicApiOriginForPage(pageOrigin: string): string {
  try {
    const origin = new URL(pageOrigin).origin;
    if (origin === productionWebOrigin) return productionApiOrigin;
    if (origin === stagingWebOrigin) return stagingApiOrigin;
    return origin;
  } catch {
    return pageOrigin;
  }
}

export function agentOnboardingSkillUrl(pageOrigin: string): string {
  return `${pageOrigin.replace(/\/$/u, "")}${agentOnboardingSkillPath}`;
}

export function agentPairingPaste(input: {
  readonly skillUrl: string;
  readonly pairingCode: string;
}): string {
  return `Read and follow ${input.skillUrl}

The pairing code is:
${input.pairingCode}

Default knowledge root: $HOME/northern-logic (Windows %USERPROFILE%\\northern-logic).
Wait until Connections shows this machine.`;
}

export function agentHarnessPaste(input: {
  readonly skillUrl: string;
}): string {
  return `Read and follow ${input.skillUrl}`;
}

export function agentEnrollCommand(apiOrigin: string): string {
  return `printf '%s\\n' "$PAIRING_CODE" | nlkb enroll \\
  --api ${apiOrigin} \\
  --root /absolute/empty/knowledge-root \\
  --headless-secret-file`;
}

function shellSingleQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

export function agentLinuxBootstrapCommand(input: {
  readonly pageOrigin: string;
  readonly apiOrigin: string;
  readonly pairingCode: string;
}): string {
  return unixBootstrapCommand(input, "$HOME/northern-logic");
}

export function agentMacosBootstrapCommand(input: {
  readonly pageOrigin: string;
  readonly apiOrigin: string;
  readonly pairingCode: string;
}): string {
  return unixBootstrapCommand(input, "$HOME/northern-logic");
}

export function agentWindowsBootstrapCommand(input: {
  readonly pageOrigin: string;
  readonly apiOrigin: string;
  readonly pairingCode: string;
}): string {
  const page = input.pageOrigin.replace(/\/$/u, "");
  const api = input.apiOrigin.replace(/\/$/u, "");
  const script = `${page}/agent-onboarding/install.sh`;
  return `curl -fsSL ${shellSingleQuote(script)} | sh -s -- ${shellSingleQuote(api)} \\
  && export PATH="\${LOCALAPPDATA:-$HOME/AppData/Local}/Northern Logic/bin:$HOME/.local/bin:$PATH" \\
  && printf '%s\\n' ${shellSingleQuote(input.pairingCode)} | nlkb enroll \\
    --api ${shellSingleQuote(api)} \\
    --root "$USERPROFILE/northern-logic" \\
    --headless-secret-file \\
  && nlkb service install \\
  && nlkb doctor`;
}

function unixBootstrapCommand(
  input: {
    readonly pageOrigin: string;
    readonly apiOrigin: string;
    readonly pairingCode: string;
  },
  rootExpression: string,
): string {
  const page = input.pageOrigin.replace(/\/$/u, "");
  const api = input.apiOrigin.replace(/\/$/u, "");
  const script = `${page}/agent-onboarding/install.sh`;
  return `curl -fsSL ${shellSingleQuote(script)} | sh -s -- ${shellSingleQuote(api)} \\
  && export PATH="$HOME/.local/bin:$PATH" \\
  && printf '%s\\n' ${shellSingleQuote(input.pairingCode)} | nlkb enroll \\
    --api ${shellSingleQuote(api)} \\
    --root "${rootExpression}" \\
    --headless-secret-file \\
  && nlkb service install \\
  && nlkb doctor`;
}

export function pairingReceiptCopies(input: {
  readonly pageOrigin: string;
  readonly apiOrigin: string;
  readonly pairingCode: string;
}): {
  readonly pairingPaste: string;
  readonly linuxBootstrapCommand: string;
  readonly macosBootstrapCommand: string;
  readonly windowsBootstrapCommand: string;
} {
  return {
    pairingPaste: agentPairingPaste({
      skillUrl: agentOnboardingSkillUrl(input.pageOrigin),
      pairingCode: input.pairingCode,
    }),
    linuxBootstrapCommand: agentLinuxBootstrapCommand(input),
    macosBootstrapCommand: agentMacosBootstrapCommand(input),
    windowsBootstrapCommand: agentWindowsBootstrapCommand(input),
  };
}

export function enrollCommandIncludesSecret(
  command: string,
  key: string,
): boolean {
  return (
    command.includes(key) ||
    command.includes("nlk_live_") ||
    command.includes("nlk_pair_")
  );
}

export function isConnectionPairingIssue(
  issued: ConnectionCreateIssue,
): issued is ConnectionPairingIssue {
  return "pairing_code" in issued;
}

export function pairingCodeExpiryTime(expiresAt: string): string {
  return new Date(expiresAt).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function pairingCodeCardNote(
  expiresAt: string,
  nowMs: number,
): { readonly title: string; readonly detail: string } {
  if (new Date(expiresAt).getTime() <= nowMs) {
    return {
      title: "Pairing code expired",
      detail: "Copy the agent prompt again to issue a new code.",
    };
  }
  return {
    title: `Pairing code pending until ${pairingCodeExpiryTime(expiresAt)}`,
    detail: "Copy the agent prompt again if you lost it.",
  };
}

export type PairingKind = "endpoint" | "agent";

export function deriveEndpointType(input: {
  readonly remoteMcp?: boolean;
  readonly ownerPrincipalType: "human" | "service" | undefined;
}): ConnectionEndpointType {
  if (input.remoteMcp) return "mcp";
  return input.ownerPrincipalType === "service" ? "service" : "local";
}

export function pairingEndpointType(input: {
  readonly kind: PairingKind;
  readonly ownerPrincipalType: "human" | "service";
}): ConnectionEndpointType {
  if (input.kind === "agent" || input.ownerPrincipalType === "service") {
    return "service";
  }
  return "local";
}

export function endpointLabel(type: ConnectionEndpointType): string {
  switch (type) {
    case "desktop":
      return "Desktop";
    case "service":
      return "Agent";
    case "mcp":
      return "Remote MCP";
    default:
      return "Machine";
  }
}

export function mcpResourceUrl(
  apiOrigin: string,
  configuredResourceUrl?: string | null,
): string {
  const configured = configuredResourceUrl?.trim() ?? "";
  if (configured.length > 0) return configured.replace(/\/$/u, "");
  return `${apiOrigin.replace(/\/$/u, "")}/mcp`;
}

export function mcpAgentPaste(input: {
  readonly mcpUrl: string;
  readonly key: string;
}): string {
  return `Add this Northern Logic Knowledge remote MCP server. Use Streamable HTTP.\n\nIf setup signs in through a browser, add this URL and complete login when asked. Do not store the connection key there.\n\nIf setup uses a static Bearer key, create the connection with this URL and the key below.\n\nDo not write the key into a skill, URL, environment variable, or vault file.\n\nThe MCP URL is:\n${input.mcpUrl}\n\nThe connection key is:\n${input.key}`;
}

export function connectionActionErrorMessage(code: string): string {
  switch (code) {
    case "replacement_not_verified":
      return "Use the new key at least once before revoking a key that has already been used.";
    case "invalid_operation":
      return "Revoke an extra key before rotating again.";
    default:
      return code.replaceAll("_", " ");
  }
}

export function mcpReceiptCopies(input: {
  readonly apiOrigin: string;
  readonly mcpResourceUrl?: string | null | undefined;
  readonly issued: ConnectionCreateIssue;
}): { readonly mcpUrl: string; readonly mcpPaste: string } | null {
  if (input.issued.connection.endpoint_type !== "mcp") return null;
  if (isConnectionPairingIssue(input.issued)) return null;
  const mcpUrl = mcpResourceUrl(input.apiOrigin, input.mcpResourceUrl);
  return {
    mcpUrl,
    mcpPaste: mcpAgentPaste({ mcpUrl, key: input.issued.key }),
  };
}

export function withoutSyncCapabilities(
  capabilities: Record<string, { can_write: boolean; can_sync: boolean }>,
): Record<string, { can_write: boolean; can_sync: boolean }> {
  return Object.fromEntries(
    Object.entries(capabilities).map(([vaultId, capability]) => [
      vaultId,
      { can_write: capability.can_write, can_sync: false },
    ]),
  );
}

export function selectedVaultCapability(localSyncAllowed: boolean): {
  readonly can_write: true;
  readonly can_sync: boolean;
} {
  return { can_write: true, can_sync: localSyncAllowed };
}

export function defaultPairingCapabilities(
  vaults: readonly {
    readonly vault_id: string;
    readonly vault_type: string;
    readonly local_sync_allowed: boolean;
    readonly owner_principal_id?: string | null;
  }[],
  ownerPrincipalId?: string,
): Record<string, { can_write: boolean; can_sync: boolean }> {
  return Object.fromEntries(
    vaults
      .filter((vault) => {
        if (vault.vault_type === "company") return true;
        if (vault.vault_type !== "personal") return false;
        if (ownerPrincipalId === undefined) return true;
        return vault.owner_principal_id === ownerPrincipalId;
      })
      .map((vault) => [
        vault.vault_id,
        selectedVaultCapability(vault.local_sync_allowed),
      ]),
  );
}

export function principalKindLabel(type: "human" | "service"): string {
  return type === "service" ? "Agent" : "Person";
}

export function ownerKindLabel(type: "human" | "service"): string {
  return principalKindLabel(type);
}

export function ownerPickerGroups<
  T extends { readonly principal_type: "human" | "service" },
>(principals: readonly T[]): { people: readonly T[]; services: readonly T[] } {
  return {
    people: principals.filter(
      (principal) => principal.principal_type === "human",
    ),
    services: principals.filter(
      (principal) => principal.principal_type === "service",
    ),
  };
}

export function visiblePairedConnections(
  connections: readonly ConnectionInventory[],
): readonly ConnectionInventory[] {
  return connections.filter((connection) => connection.state !== "revoked");
}

export function groupConnectionsByOwner(
  connections: readonly ConnectionInventory[],
  currentPrincipalId: string,
): readonly ConnectionOwnerGroup[] {
  const groups = new Map<string, ConnectionOwnerGroup>();
  for (const connection of connections) {
    const existing = groups.get(connection.owner_principal_id);
    if (existing === undefined) {
      groups.set(connection.owner_principal_id, {
        owner_principal_id: connection.owner_principal_id,
        owner_display_name: connection.owner_display_name,
        owner_principal_type: connection.owner_principal_type,
        connections: [connection],
      });
      continue;
    }
    groups.set(connection.owner_principal_id, {
      ...existing,
      connections: [...existing.connections, connection],
    });
  }
  return [...groups.values()].sort((left, right) => {
    if (left.owner_principal_id === currentPrincipalId) return -1;
    if (right.owner_principal_id === currentPrincipalId) return 1;
    if (left.owner_principal_type !== right.owner_principal_type) {
      return left.owner_principal_type === "human" ? -1 : 1;
    }
    return left.owner_display_name.localeCompare(right.owner_display_name);
  });
}
